// [2026-09-02] ADDED — the customer's proctoring reports: every session, and one session in full.
//
// TWO LEVELS, ONE SCREEN. The table answers "which candidates ran an exam and what happened", the
// modal answers "what exactly did THIS candidate do". Both are scoped server-side to the signed-in
// company — `/me/sessions/{id}` puts the client_id in the query rather than checking it after the
// read, so another company's session id returns a 404 and not their report.
//
// THE TRUST NOTE IS RENDERED, NOT HIDDEN. The API returns `trust_note` saying browser telemetry can
// be suppressed or faked by a determined candidate, and that the authoritative signal is the video
// analysis. A reviewer is about to make a decision about a real person on this screen; showing the
// events while omitting their known limitation would be the dishonest version of this UI.

import { useEffect, useState } from 'react'
// [2026-09-03] CHANGED — platformEvidenceObjectUrl added to the import.
// OLD: import { platform } from '../../api.js'
import { platform, platformEvidenceObjectUrl } from '../../api.js'
// [2026-09-05] CHANGED — this file now renders the Pf* primitives throughout.
// OLD: import { Empty, Modal, Notice, Skeleton, num, when } from '../../ui.jsx'
//      import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
//        from '../../components/ui/table.jsx'
// `clock` comes from ui.jsx too — it mirrors app/core/timeline.py:clock, so an elapsed offset reads
// identically in the API and the UI. Both shared modules are UNCHANGED and still exported.
import { clock, num, when } from '../../ui.jsx'
// [2026-09-04] CHANGED — the Badge import is dropped; every usage in this file is now PfChip.
// badge.jsx is UNTOUCHED and still exported — the admin app imports it.
// OLD: import { Badge } from '../../components/ui/badge.jsx'
import DataTable from './DataTable.jsx'
// [2026-09-16] CHANGED — CameraIcon, MicIcon and SpeakerIcon added for the Device Information card.
// Every previous import is kept: ListIcon and MonitorIcon still head the two timeline/screenshot
// cards, and ReportIcon is still the table's row control.
// OLD: import {
//        CalendarIcon, ClockIcon, DownloadIcon, EyeIcon, ListIcon, MonitorIcon, ReportIcon,
//      } from '../../components/platform/icons.jsx'
import {
  CalendarIcon, CameraIcon, ClockIcon, DownloadIcon, EyeIcon, ListIcon, MicIcon, MonitorIcon,
  ReportIcon, SpeakerIcon,
} from '../../components/platform/icons.jsx'
import {
  PfChip, PfEmpty, PfModal, PfNotice, PfSkeleton,
} from '../../components/platform/ui.jsx'
import logoUrl from '../../assets/incruiter-logo.svg'

// [2026-09-04] CHANGED — PfChip instead of the shared shadcn Badge.
// Badge reads theme.css's status palette, and proctoring-frontend-admin imports badge.jsx
// DIRECTLY (its Staff.jsx and Diagnostics.jsx), so recolouring it to the IncBot palette would
// repaint the staff dashboard. PfChip is scoped to this app and carries the product tokens, so
// the two dashboards each keep their own consistent palette.
// OLD: const STATE_VARIANT = { active: 'healthy', paused: 'degraded', ended: 'idle' }
//      const SEVERITY_VARIANT = { high: 'critical', medium: 'serious', low: 'degraded' }
const STATE_TONE = { active: 'live', paused: 'warn', ended: 'idle' }
const SEVERITY_TONE = { high: 'bad', medium: 'warn', low: 'idle' }

// [2026-09-16] ADDED — the Proctoring Logs report.
//
// Asked for by Rujal on 2026-09-16, against the InCbot interview report's Proctoring Logs tab
// (frontend-feedback/src/components/pages/incbotFeedback/views/ProctoringLogsView.tsx). Two
// columns: the timeline on the left, the hardware and the frames on the right.
//
// WHAT IS DELIBERATELY NOT COPIED FROM THAT SCREEN, because this product does not have the data and
// printing a number we do not hold on the page where somebody decides whether a candidate cheated
// would be a fabrication:
//   · the candidate's NAME, photo, email and phone — this API returns `reference_id`, the
//     customer's own id for that person, and nothing else identifying (see ReportModal below)
//   · "88% Resume Match", "Cautiously Recommended", "Deepfake Not Detected" — all three are
//     InCbot interview-scoring outputs. This service proctors; it does not score an interview.
//   · the six tabs — that report has six sections. This one has this section.
// Each of those is a real InCbot feature reading real InCbot data. None of them has a source here.

// ── event sentences ───────────────────────────────────────────────────────────
//
// The design writes full sentences ("Candidate looking away from screen for 37 seconds"); the API
// returns machine types (`tab_hidden`). This is the translation, and it is ONLY a translation — no
// row appears that the API did not send, and no row says more than its type means.
//
// KEYS ARE THE SERVER'S OWN TYPE TABLE, app/modules/browser/route.py:110-157. An unknown type falls
// through to the previous rendering (underscores to spaces) rather than being dropped, so an SDK
// that grows an event before this map does still shows it — a proctoring UI that silently omits a
// signal is worse than one that renders it plainly.
const EVENT_PHRASE = {
  tab_hidden: 'Candidate switched away from the exam tab',
  tab_visible: 'Candidate returned to the exam tab',
  window_blur: 'Exam window lost focus',
  window_focus: 'Exam window regained focus',
  fullscreen_entered: 'Fullscreen mode enabled',
  fullscreen_exited: 'Candidate left fullscreen mode',
  page_unload: 'Exam page was closed or reloaded',

  camera_started: 'Candidate switched ON their camera',
  camera_stopped: 'Candidate switched OFF their camera',
  camera_interrupted: 'Camera stopped unexpectedly',
  camera_permission_denied: 'Camera permission denied',
  microphone_started: 'Candidate switched ON their microphone',
  microphone_stopped: 'Candidate switched OFF their microphone',
  microphone_interrupted: 'Microphone stopped unexpectedly',
  microphone_permission_denied: 'Microphone permission denied',
  screen_share_started: 'Screen sharing started',
  screen_share_stopped: 'Screen sharing stopped',
  screen_share_interrupted: 'Screen sharing stopped unexpectedly',
  screen_share_permission_denied: 'Screen sharing permission denied',
  devices_changed: 'Connected devices changed',
  environment_reported: 'Device and system details reported',

  network_offline: 'Candidate went offline',
  network_online: 'Candidate came back online',
  session_started: 'Interview started',
  session_stopped: 'Interview ended',
  session_paused: 'Proctoring paused',
  session_resumed: 'Proctoring resumed',
  heartbeat: 'Session heartbeat',

  copy: 'Candidate copied content',
  paste: 'Candidate pasted content',
  context_menu: 'Candidate opened the right-click menu',
  print: 'Candidate opened the print dialog',

  sdk_error: 'Proctoring reported an internal error',
}

function phraseOf(type) {
  return EVENT_PHRASE[type] || String(type || 'event').replace(/_/g, ' ')
}

/** "Sep 16, 2026" — the date heading above the timeline. */
function dayLabel(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** "12:02 pm" — lower-cased, as the design draws it. */
function timeLabel(iso) {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    .toLowerCase()
}

/** "Sep 16, 2026 12:03 pm" — the stamp under a screenshot. */
function stampLabel(iso) {
  const day = dayLabel(iso)
  return day ? `${day} ${timeLabel(iso)}` : timeLabel(iso)
}

// ── [2026-09-16] ADDED — which clock to believe ───────────────────────────────
//
// THE BUG THIS FIXES, seen on deployed test: a session that ran for 1.4 seconds at 19:16 drew a
// timeline reading 06:35 am, 07:35 am, 08:35 am … 01:35 pm. Eight events, one hour apart, all of
// them BEFORE the session existed. The screen was not broken — it was faithfully rendering
// `occurred_at`, and `occurred_at` was nonsense.
//
// IT IS THE LEAST TRUSTWORTHY OF THE THREE TIMES WE HOLD, and the server says so at the field
// itself (app/modules/browser/route.py:253-255): "wall-clock time on the candidate's machine is
// adjustable — by NTP, by daylight saving, or by a candidate who wants their timeline to look
// different." Rendering it unchecked puts the one clock the candidate controls on the one screen
// where a decision gets made about them. A laptop with a skewed clock — which is ordinary, not
// malicious — produces exactly the screen above, in production, for a real candidate.
//
// SO EACH EVENT IS TIMED FROM THE BEST SOURCE IT ACTUALLY HAS:
//
//   1. elapsed_ms  — monotonic milliseconds since the SDK started, carried BESIDE occurred_at for
//                    precisely this reason. It only ever moves forward and changing the system
//                    clock does not touch it. Anchored to the session's own created_at, which is
//                    our timestamp, not theirs.
//   2. occurred_at — believed when it lands inside the session. Within the window it is the most
//                    PRECISE answer: it is when the thing happened in the browser, and delivery can
//                    lag it by minutes when a candidate drops offline mid-exam.
//   3. received_at — our server's clock, stamped as the batch arrived. Never wrong, only late.
//
// NOTHING IS HIDDEN AND NOTHING IS DROPPED. Every event still renders; when the browser's own
// timestamp was not believable the row says so on hover and is marked, rather than quietly
// swapping one number for another. A reviewer who wonders why a time looks odd gets an answer.

// How far outside the session a browser timestamp may fall and still be believed. Generous on
// purpose: clocks drift by seconds, and an event at the very edge of a session is ordinary.
const CLOCK_GRACE_MS = 60_000

function resolveClock(row, session) {
  const started = session?.created_at ? new Date(session.created_at).getTime() : NaN
  const ended = session?.ended_at ? new Date(session.ended_at).getTime() : Date.now()
  const claimed = row?.occurred_at || row?.captured_at
  const claimedMs = claimed ? new Date(claimed).getTime() : NaN
  const receivedMs = row?.received_at ? new Date(row.received_at).getTime() : NaN

  if (Number.isFinite(started) && row?.elapsed_ms != null && Number.isFinite(Number(row.elapsed_ms))) {
    return { ms: started + Number(row.elapsed_ms), trusted: true,
             why: 'Timed from the SDK’s own elapsed counter, anchored to the session start.' }
  }
  if (Number.isFinite(claimedMs)
      && (!Number.isFinite(started) || claimedMs >= started - CLOCK_GRACE_MS)
      && claimedMs <= ended + CLOCK_GRACE_MS) {
    return { ms: claimedMs, trusted: true, why: 'Reported by the candidate’s browser.' }
  }
  if (Number.isFinite(receivedMs)) {
    return { ms: receivedMs, trusted: false,
             why: claimed
               ? `Shown as received by our server. The browser reported ${stampLabel(claimed)}, `
                 + 'which falls outside this session — the candidate’s device clock is wrong or '
                 + 'was changed.'
               : 'Shown as received by our server; the browser sent no timestamp.' }
  }
  // Nothing usable at all. Believe the claim rather than showing a dash: an odd time a reviewer can
  // question beats a blank they cannot.
  return { ms: Number.isFinite(claimedMs) ? claimedMs : NaN, trusted: false,
           why: 'No server receipt time recorded for this row.' }
}

/** Events in the order they should be READ, timed by resolveClock.
 *
 *  RE-SORTED HERE, and it has to be: the server sorts on `occurred_at`
 *  (app/core/browser_store.py:232), so a skewed device clock scrambles the order as well as the
 *  times. Leaving the server's order while showing a different clock would print a column of
 *  timestamps that jump backwards, which looks like a rendering fault rather than a device fault.
 *
 *  Ties keep their original position — `index` is the last comparison — so two events in the same
 *  millisecond stay in the order the SDK queued them.
 */
function timelineOf(events, session) {
  const sorted = events
    .map((e, index) => ({ e, index, clock: resolveClock(e, session) }))
    .sort((a, b) => {
      const av = Number.isFinite(a.clock.ms) ? a.clock.ms : Infinity
      const bv = Number.isFinite(b.clock.ms) ? b.clock.ms : Infinity
      return av - bv || a.index - b.index
    })
  // [2026-09-16] ADDED — see anchorStart. Applied after the sort, never instead of it.
  return anchorStart(sorted)
}

// [2026-09-16] ADDED — "Interview started" must not sit below the media it waited for.
//
// WHAT WAS ON SCREEN, on all five real sittings run against deployed test on 2026-09-16:
//
//     Candidate switched ON their camera        <- first
//     Interview started                         <- second
//
// FIVE OUT OF FIVE, with a gap of 0 or 1 MILLISECOND. This is not a race and not a timestamp
// problem — the SDK reports it that way by construction (proctoring-sdk/src/client.ts:312-319):
// `await this.camera.start()` runs, and only then is SESSION_STARTED reported, because the session
// is not started until the media it needs is up. Two statements apart, so the two events land in
// the same millisecond. Every candidate, every interview, every client.
//
// WHY IT IS WORTH TOUCHING A LIVE PRODUCT FOR. A recruiter does not read this as an ordering
// convention; they read it as "the camera came on before the interview began", which is
// impossible, on the one screen where a person decides something about another person. The cost is
// not a support ticket — it is quiet doubt about the whole report.
//
// AND NOTHING TRUE IS LOST. A 0-1ms ordering was never information: it recorded which line of our
// own code ran first. This decides it rather than leaving it to insertion order.
//
// THE RULE IS THE ONE INCRUITER ALREADY CHOSE for the same problem in the InCbot report
// (frontend-feedback/src/utils/helper.tsx:1632, orderProctoringMessages): "only the offending rows
// move, and only to directly below the start row. Every other row keeps its time-sorted position."
//
// SO ONLY THE MEDIA-START ROWS MOVE. In particular `window_blur` does NOT: on a first-time
// candidate the camera permission prompt takes focus and fires a real blur ~600ms before the
// session starts, and that genuinely happened before it. Reordering it would be re-writing the
// timeline rather than settling a tie.
//
// A FIRST ATTEMPT AT THIS WAS WRONG AND IS RECORDED SO IT IS NOT RETRIED: "move the start row above
// anything within one second of it" read better on four sittings and worse on the fifth, where it
// wedged the start row between a blur and its own matching focus:
//     Exam window lost focus / Interview started / Exam window regained focus
// Breadth was the bug. Narrow to the rows that are demonstrably an artefact.
const MEDIA_START_TYPES = new Set([
  'camera_started', 'microphone_started', 'screen_share_started',
])

function anchorStart(rows) {
  const at = rows.findIndex((r) => r.e.type === 'session_started')
  // -1: no start row to anchor against. 0: already first, so nothing can be above it. Either way
  // the time-sorted order stands untouched — the common case is a no-op.
  if (at <= 0) return rows
  const before = rows.slice(0, at)
  const misplaced = before.filter((r) => MEDIA_START_TYPES.has(r.e.type))
  if (misplaced.length === 0) return rows
  const kept = before.filter((r) => !MEDIA_START_TYPES.has(r.e.type))
  return [...kept, rows[at], ...misplaced, ...rows.slice(at + 1)]
}

/** The three device rows, or [] when the SDK never sent a snapshot.
 *
 *  THE ACTIVE DEVICE FIRST, the enumerated list second. `media.camera.label` is the device the
 *  browser was actually receiving from; `devices.camera_labels[0]` is merely the first one attached.
 *  When a candidate has a laptop camera and a webcam plugged in, those are different answers, and
 *  the one worth showing on a proctoring report is the one that was on.
 *
 *  EMPTY ROWS ARE DROPPED rather than rendered as "—", matching the source design's own
 *  `.filter(item => item.value)`. A browser blanks every device name until media permission is
 *  granted, so a blank here means "we were not allowed to look", and a dash beside "Microphone"
 *  reads as "no microphone" — the wrong conclusion, on the screen where conclusions get drawn.
 */
function devicesOf(environment) {
  if (!environment) return []
  const devices = environment.devices || {}
  const media = environment.media || {}
  return [
    { label: 'Camera', Icon: CameraIcon,
      value: media.camera?.label || devices.camera_labels?.[0] },
    { label: 'Microphone', Icon: MicIcon,
      value: media.microphone?.label || devices.microphone_labels?.[0] },
    { label: 'Speaker', Icon: SpeakerIcon,
      value: devices.speaker_labels?.[0] },
  ].filter((row) => !!row.value)
}

// [2026-09-05] CHANGED — the report is rebuilt to the IncProctor Figma's "Prcotoring Report"
// frame (node-id 17-6980): candidate header with the InCruiter logo, teal section bands, a Score
// Overview of five colour-ruled tiles, the timeline, the screenshot grid, then the disclaimer.
//
// NOTHING NEW IS FETCHED and nothing is invented. Every value below comes from the same
// GET /me/sessions/{id} the previous version rendered; the design just shows more of it. Where the
// mock shows a field this API does not have, that is called out at the point it would have gone.
//
// The previous render is kept at the bottom of this file, commented out and date-stamped.

// [2026-09-03] ADDED — one captured screenshot.
//
// The report has always carried the evidence INDEX (build_report returns it), so this modal could
// say "Evidence: 1" while showing no picture at all. The missing half was a way to read the BYTES
// as the signed-in user; that is the new GET /me/sessions/{id}/evidence/{event_id}.
//
// The frames live in a PRIVATE Azure container and are streamed through the API, never linked
// directly — see the note on platformEvidenceObjectUrl in api.js for why an <img src> pointing at
// blob storage is both broken (no auth header) and wrong (a permanent public link to a candidate's
// screen).
//
// ABORT AND REVOKE ON UNMOUNT, both. Closing the modal mid-load must cancel the request, and the
// object URL pins a decoded image in memory until it is revoked — a session with sixty frames would
// otherwise hold sixty of them for the life of the page. This mirrors EvidenceFrame in
// views/Candidates.jsx, which solves the identical problem against the old gateway API.
//
// [2026-09-05] CHANGED — restyled as the Figma's screenshot card, and given the two controls it
// draws: view (a lightbox) and download. Both reuse the object URL ALREADY fetched for the
// thumbnail, so neither costs a second request for bytes the browser is holding.
// [2026-09-16] CHANGED — `session` added, so a frame's stamp goes through the same clock check the
// timeline does. Evidence carries the identical pair of times (`captured_at` from the browser,
// `received_at` from us), so it has the identical problem.
// OLD: function EvidenceFrame({ sessionId, frame, index, event, onView }) {
function EvidenceFrame({ sessionId, session, frame, index, event, onView }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let objectUrl = null
    platformEvidenceObjectUrl(sessionId, frame.event_id, controller.signal)
      .then((value) => { objectUrl = value; setUrl(value) })
      .catch((err) => { if (err.name !== 'AbortError') setFailed(true) })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [sessionId, frame.event_id])

  // [2026-09-16] CHANGED — the caption is now the event SENTENCE, coloured by severity, over the
  // design's "Sep 16, 2026 12:03 pm" stamp. Same two facts as before (which event, when), written
  // the way the InCbot report writes them.
  //
  // THE COLOUR IS THE EVENT'S OWN SEVERITY, not a fixed orange. The source design hardcodes a tone
  // per card; here the API already grades every event high/medium/low, so a frame captured for a
  // tab switch and one captured for a heartbeat do not get the same red.
  //
  // --- OLD (pre-2026-09-16) ---
  // const label = event ? String(event.type).replace(/_/g, ' ')
  //   : String(frame.kind || 'screen').replace(/_/g, ' ')
  // const at = event?.elapsed_ms != null
  //   ? ` at ${clock(event.elapsed_ms / 1000)} into the session` : ''
  // --- END OLD ---
  const label = event ? phraseOf(event.type)
    : String(frame.kind || 'screen').replace(/_/g, ' ')
  // Kept from the old caption: how far into the exam this was. The design has no equivalent, and it
  // is the one thing a wall-clock stamp cannot tell you when a session started at an odd minute.
  const at = event?.elapsed_ms != null ? ` at ${clock(event.elapsed_ms / 1000)} into the session` : ''
  const tone = { high: '#f2464b', medium: '#e8821e', low: '#18ac00' }[event?.severity] || undefined
  // The frame's own times, checked the same way the timeline's are. `event` is preferred when the
  // frame is joined to one: the event carries `elapsed_ms`, the frame does not, and that is the
  // only source no device clock can touch.
  const shotAt = resolveClock(event && event.elapsed_ms != null ? event : frame, session)

  return (
    <figure className="pf-shot">
      <div className="pf-shot-img">
        {failed
          // A frame whose bytes are gone is stated, not left as a broken-image icon. The pointer
          // outliving the blob is a real case — storage lifecycle rules expire frames.
          ? <div className="pf-shot-placeholder">Frame unavailable</div>
          : url
            ? <img src={url} alt={`Captured ${frame.kind || 'screen'} frame`} loading="lazy" />
            : <div className="pf-shot-placeholder">Loading…</div>}
      </div>
      <figcaption>
        <div className="t">Screenshot #{index + 1}</div>
        {/* [2026-09-16] CHANGED — severity colour, and no capitalize: the phrase is already a
            sentence, and text-transform would leave "Candidate Switched Away From The Exam Tab".
            OLD: <div className="d" style={{ textTransform: 'capitalize' }}>{label}{at}</div> */}
        <div className="d" style={tone ? { color: tone } : undefined}>{label}{at}</div>
        <div className="pf-shot-foot">
          {/* [2026-09-16] CHANGED — the design's stamp format, on the resolved clock.
              OLD: <span className="when"><ClockIcon />{when(frame.captured_at)}</span> */}
          <span className={`when${shotAt.trusted ? '' : ' adjusted'}`} title={shotAt.why}>
            <ClockIcon />
            {Number.isFinite(shotAt.ms) ? stampLabel(new Date(shotAt.ms).toISOString()) : '—'}
          </span>
          {url && (
            <span className="pf-shot-acts">
              <button type="button" onClick={() => onView(url)}
                      title="View full size" aria-label={`View screenshot ${index + 1} full size`}>
                <EyeIcon />
              </button>
              {/* A plain download anchor over the object URL the thumbnail already holds. The
                  filename carries the session and event so a saved frame is still identifiable
                  once it is sitting in someone's Downloads folder. */}
              <a href={url} download={`${sessionId}-${frame.event_id}.jpg`}
                 title="Download" aria-label={`Download screenshot ${index + 1}`}>
                <DownloadIcon />
              </a>
            </span>
          )}
        </div>
      </figcaption>
    </figure>
  )
}

/** Initials for the report header. Derived from the customer's own reference id, which is the only
 *  identifier this API carries — see the note in ReportModal about the candidate's NAME. */
function initialsOf(text) {
  const parts = String(text || '').split(/[^A-Za-z0-9]+/).filter(Boolean)
  if (!parts.length) return '—'
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
}

/** How long the session ran, as the Figma's "13m 36s". Absent while a session is still live. */
function duration(from, to) {
  if (!from || !to) return null
  const ms = new Date(to).getTime() - new Date(from).getTime()
  if (!Number.isFinite(ms) || ms <= 0) return null
  return clock(ms / 1000)
}

/** The full report for one session, fetched when the row is opened. */
function ReportModal({ sessionId, onClose, onError }) {
  const [state, setState] = useState({ report: null, error: null, loading: true })
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => {
    let live = true
    platform.session(sessionId)
      .then((report) => { if (live) setState({ report, error: null, loading: false }) })
      .catch((err) => {
        if (!live) return
        onError?.(err)
        setState({ report: null, error: err, loading: false })
      })
    return () => { live = false }
  }, [sessionId, onError])

  // Escape closes the lightbox FIRST, and only then the modal — otherwise viewing a frame and
  // pressing Escape would shut the whole report and lose the reviewer's place.
  useEffect(() => {
    if (!lightbox) return undefined
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setLightbox(null) } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [lightbox])

  const { report, error, loading } = state
  const session = report?.session || {}
  const counts = report?.counts || {}
  const events = report?.events || []
  const evidence = report?.evidence || []

  // Join each frame to the event that produced it, so a screenshot can say what it is evidence OF.
  const eventById = new Map(events.map((e) => [e.event_id, e]))

  const ran = duration(session.created_at, session.ended_at)
  const ref = session.reference_id || sessionId

  // [2026-09-16] ADDED — the candidate's self-reported hardware, for the Device Information card.
  //
  // OPTIONAL BY DESIGN, and the empty case is a MISSING key rather than an empty object: the server
  // omits it entirely when the SDK never sent a snapshot (an older SDK, `environment: {enabled:
  // false}`, or a session that ended before its first flush). `devicesOf` turns that into [], and
  // the card below is not rendered at all — the same choice the source design makes.
  const devices = devicesOf(report?.environment)

  // [2026-09-16] ADDED — events timed and ordered by the clock we can actually trust.
  const timeline = timelineOf(events, session)

  return (
    <PfModal wide onClose={onClose}
             title="Proctoring report"
             subtitle={report?.generated_at ? `Generated ${when(report.generated_at)}` : undefined}>
      {loading && <PfSkeleton height={320} />}
      {error && <PfNotice kind="error">{error.message}</PfNotice>}
      {report && (
        <div className="pf-report">
          {/* ── who ────────────────────────────────────────────────────────
              THE CANDIDATE'S NAME IS NOT SHOWN, because this API does not have one. The mock shows
              "Mitesh Pokar"; GET /me/sessions/{id} returns `reference_id` — the customer's OWN id
              for that person — and nothing else identifying. Printing a name we do not hold, or
              guessing one from the reference, would be a fabrication on the screen where somebody
              decides whether a candidate cheated. The reference id is the honest heading. */}
          <div className="pf-report-head">
            <div className="pf-report-avatar" aria-hidden="true">{initialsOf(ref)}</div>
            <div className="pf-report-who">
              <h3 title={ref}>{ref}</h3>
              <div className="pf-report-meta">
                <span><CalendarIcon />{when(session.created_at)}</span>
                {ran && <span><ClockIcon />{ran}</span>}
                {session.origin && <span className="pf-mono">{session.origin}</span>}
              </div>
            </div>
            <img className="pf-report-logo" src={logoUrl} alt="InCruiter" />
          </div>

          {/* [2026-09-16] CHANGED — the body below is rebuilt to the InCbot report's Proctoring
              Logs layout, asked for by Rujal on 2026-09-16 so the two InCruiter reports read the
              same way.

              WHAT WENT, AND WHERE IT WENT:
                · "Session Report" band + Score Overview — the five tiles and the severity chips.
                  Every figure on them is still on screen: Events and Evidence are columns on the
                  table this modal opens from, Started / Ended / duration are in the header two
                  lines above, and State is the chip on the row you clicked. The tiles repeated
                  them; the design does not have them.
                · the Timeline TABLE — replaced by the same events as a timeline. Identical rows,
                  identical order, nothing filtered. The `Type` column becomes a sentence and the
                  `Severity` chip becomes the coloured dot beside it. `Elapsed` is the one column
                  with no home in the new design; it is still on every screenshot caption.

              THE OLD RENDER IS KEPT BELOW, commented out and whole. Restoring the previous report
              is uncommenting this block and deleting nothing — which is the point of leaving it.

              --- OLD (pre-2026-09-16) ---
              <div className="pf-report-band">Session Report</div>

              <div className="pf-report-card">
                <div className="pf-report-card-head"><ReportIcon />Score Overview</div>
                <div className="pf-score-grid">
                  {[
                    { k: 'Events',   v: num(counts.events || 0),   c: '#133f7d', big: true },
                    { k: 'Evidence', v: num(counts.evidence || 0), c: '#18ac00', big: true },
                    { k: 'State',    v: session.state || '—',      c: '#2a78d6', big: true },
                    { k: 'Started',  v: when(session.created_at),  c: '#e8aa4e' },
                    { k: 'Ended',    v: session.ended_at ? when(session.ended_at) : 'Still running',
                      c: '#f2464b' },
                  ].map((t) => (
                    <div className="pf-score-tile" key={t.k} style={{ borderTopColor: t.c }}>
                      <div className="k">{t.k}</div>
                      <div className={t.big ? 'v' : 'v small'}
                           style={t.big ? { color: t.c, textTransform: 'capitalize' } : undefined}>
                        {t.v}
                      </div>
                    </div>
                  ))}
                </div>

                {Object.keys(counts.by_severity || {}).length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
                    {Object.entries(counts.by_severity).map(([sev, n]) => (
                      <PfChip key={sev} tone={SEVERITY_TONE[sev] || 'idle'}>{sev}: {num(n)}</PfChip>
                    ))}
                  </div>
                )}
              </div>

              <div className="pf-report-card">
                <div className="pf-report-card-head"><ListIcon />Timeline ({num(events.length)})</div>
                {events.length === 0 ? (
                  <PfEmpty title="No events recorded"
                           hint="This session reported no browser events." />
                ) : (
                  <div className="pf-table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
                    <table className="pf-table">
                      <thead>
                        <tr>
                          <th>Request Date &amp; Time</th><th>Type</th>
                          <th className="num">Elapsed</th><th className="num">Severity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((e) => (
                          <tr key={e.event_id}>
                            <td className="pf-muted">{when(e.occurred_at)}</td>
                            <td style={{ textTransform: 'capitalize' }}>
                              {String(e.type).replace(/_/g, ' ')}
                            </td>
                            <td className="num pf-muted">
                              {e.elapsed_ms == null ? '—' : clock(e.elapsed_ms / 1000)}
                            </td>
                            <td className="num">
                              <PfChip tone={SEVERITY_TONE[e.severity] || 'idle'}>{e.severity}</PfChip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="pf-report-band">Proctoring Logs</div>

              <div className="pf-report-card">
                <div className="pf-report-card-head">
                  <MonitorIcon />Proctoring Events Screenshots ({num(evidence.length)})
                </div>
                {evidence.length === 0 ? (
                  <PfEmpty title="No screenshots captured"
                           hint="This session recorded events but no image frames." />
                ) : (
                  <div className="pf-shot-grid">
                    {evidence.map((frame, i) => (
                      <EvidenceFrame key={frame.event_id} sessionId={sessionId} frame={frame}
                                     index={i} event={eventById.get(frame.event_id)}
                                     onView={setLightbox} />
                    ))}
                  </div>
                )}
              </div>
              --- END OLD --- */}

          <div className="pf-report-band">Proctoring Logs</div>

          <div className="pf-logs">
            {/* ── left: the event timeline ───────────────────────────────
                ONE ROW PER EVENT, IN ORDER, AND NOTHING DROPPED. The server returns them sorted;
                this renders them. It is tempting to hide `heartbeat` to shorten the list — do not.
                A proctoring timeline with quiet rows removed is a timeline whose gaps no longer
                mean anything, and "the SDK went silent for nine minutes" is a finding. */}
            <div className="pf-report-card">
              <div className="pf-report-card-head"><MonitorIcon />Proctoring Events Log</div>
              {events.length === 0 ? (
                <PfEmpty title="No events recorded"
                         hint="This session reported no browser events." />
              ) : (
                <>
                  <div className="pf-logs-date">{dayLabel(session.created_at)}</div>
                  <div className="pf-logs-rail">
                    {timeline.map(({ e, clock: at }) => (
                      <div className="pf-logs-row" key={e.event_id}>
                        {/* [2026-09-16] CHANGED — the resolved clock, not the browser's raw claim.
                            `adjusted` marks a row whose device timestamp was not believable, and
                            the title says which clock this is and why. See resolveClock.
                            OLD: <div className="pf-logs-time">{timeLabel(e.occurred_at)}</div> */}
                        <div className={`pf-logs-time${at.trusted ? '' : ' adjusted'}`}
                             title={at.why}>
                          {Number.isFinite(at.ms) ? timeLabel(new Date(at.ms).toISOString()) : '—'}
                        </div>
                        {/* The dot is decoration WITH a title, never the only carrier of severity:
                            the sentence beside it says what happened, and the title says how it was
                            graded. Colour alone would make this row unreadable to a reviewer who
                            cannot separate red from orange. */}
                        <span className={`pf-logs-dot ${e.severity || 'idle'}`}
                              title={`${e.severity || 'unknown'} severity`} />
                        <div className="pf-logs-text">{phraseOf(e.type)}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* ── right: hardware, then the frames ───────────────────────── */}
            <div className="pf-logs-col">
              {/* RENDERED ONLY WHEN THE SDK SENT A SNAPSHOT. An empty card headed "Device
                  Information" would read as "we checked and found no hardware", which is the one
                  wrong conclusion a reviewer could draw from an absence here. */}
              {devices.length > 0 && (
                <div className="pf-report-card">
                  <div className="pf-report-card-head">
                    <span className="pf-head-ico"><MonitorIcon /></span>Device Information
                  </div>
                  <div className="pf-dev-grid">
                    {devices.map(({ label, value, Icon }) => (
                      <div className="pf-dev" key={label}>
                        <div className="pf-dev-ico"><Icon /></div>
                        <div className="pf-dev-txt">
                          <div className="k">{label}</div>
                          {/* `title` because the value is truncated to one line — a device name
                              like "Default - Speakers (Realtek(R) Audio)" does not fit a half
                              column, and hovering must still give the whole string. */}
                          <div className="v" title={value}>{value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pf-report-card">
                <div className="pf-report-card-head">
                  <CameraIcon />Proctoring Events Screenshots
                </div>
                {evidence.length === 0 ? (
                  // Said plainly rather than rendering an empty grid. "No screenshots" and
                  // "screenshots failed to load" are different problems and a blank space looks
                  // like the second one.
                  <PfEmpty title="No screenshots captured"
                           hint="This session recorded events but no image frames." />
                ) : (
                  // Same <EvidenceFrame> as before — same authenticated fetch, same view and
                  // download controls, re-flowed two-across by .pf-logs-shots.
                  <div className="pf-logs-shots">
                    {evidence.map((frame, i) => (
                      <EvidenceFrame key={frame.event_id} sessionId={sessionId} session={session}
                                     frame={frame} index={i}
                                     event={eventById.get(frame.event_id)}
                                     onView={setLightbox} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── the disclaimer ────────────────────────────────────────────
              LAST, AND ALWAYS RENDERED. The API's own words, verbatim — build_report returns this
              string precisely so every consumer says the same thing. A reviewer has just looked at
              photographs of a candidate; the sentence explaining that browser telemetry can be
              suppressed or faked is the one that must not be optional.

              The Figma also carries a second, italic line about answers under 150 words and
              AI-assistance detection. That is NOT reproduced: this product does not score written
              answers, so the sentence would describe a feature that does not exist here. It belongs
              to the interview-feedback report the design was adapted from. */}
          {/* [2026-09-25] CHANGED — the API's trust note is no longer shown on the report. Asked for
              by Rujal: the "can be suppressed or fabricated" wording is not something a client
              should read on their report. The API still RETURNS `trust_note` (build_report is
              unchanged), so an integrator reading the JSON still gets it; only this screen stops
              printing it. `false &&` so it is one edit away from coming back.
              OLD: {report.trust_note && <PfNotice kind="warn">{report.trust_note}</PfNotice>} */}
          {false && report.trust_note && <PfNotice kind="warn">{report.trust_note}</PfNotice>}
        </div>
      )}

      {lightbox && (
        <div className="pf-lightbox" onClick={() => setLightbox(null)} role="presentation">
          <img src={lightbox} alt="Captured frame, full size" />
        </div>
      )}
    </PfModal>
  )
}

export default function PlatformReports({ onError }) {
  const [openId, setOpenId] = useState(null)

  // [2026-09-05] CHANGED — same mapping as PlatformRequests. `order` additionally accepts "name"
  // here, which the sessions endpoint sorts by the customer's own reference_id.
  // OLD: async function fetchPage({ limit, offset, search, state }) {
  async function fetchPage({ limit, offset, search, state, dateFrom, dateTo, order }) {
    try {
      const d = await platform.sessions({ limit, offset, search, state,
                                          date_from: dateFrom, date_to: dateTo, order })
      return { rows: d.sessions || [], total: d.total || 0 }
    } catch (err) { onError?.(err); throw err }
  }

  const columns = [
    {
      key: 'reference_id',
      label: 'Candidate ref',
      render: (r) => <span className="mono">{r.reference_id || r.session_id.slice(0, 14) + '…'}</span>,
    },
    // [2026-09-11] ADDED — the analysis belonging to this candidate session. Asked for by Rujal:
    // this table showed a candidate reference with no way to tell which analysis job went with it,
    // so the two halves of one interview could not be lined up on screen.
    //
    // THEY COME FROM A JOIN, not from the session. browser_sessions and video_jobs are written by
    // different things at different times, linked only by session_id; /me/sessions now attaches
    // these three (app/core/me_analytics.py:attach_video_jobs).
    //
    // "—" IS A REAL ANSWER HERE and not a gap to be tidied away: a session with no job means the
    // exam happened and the recording was never submitted for analysis. On this account that is
    // most of the 441 rows, and it is worth being able to see at a glance.
    //
    // `job_room_id` / `job_interview_id`, NOT `room_id` / `interview_id` — prefixed on the server so
    // they cannot be confused with a field of the session itself. These are the ANALYSIS's
    // identifiers; the session's own customer reference is `reference_id` in the first column.
    {
      key: 'job_id',
      label: 'Job ID',
      render: (r) => (r.job_id
        ? <span className="mono">{r.job_id}</span>
        : <span className="muted">—</span>),
    },
    {
      key: 'job_room_id',
      label: 'Room',
      render: (r) => (r.job_room_id
        ? <span className="mono">{r.job_room_id}</span>
        : <span className="muted">—</span>),
    },
    {
      key: 'job_interview_id',
      label: 'Interview',
      render: (r) => (r.job_interview_id
        ? <span className="mono">{r.job_interview_id}</span>
        : <span className="muted">—</span>),
    },
    {
      key: 'state',
      label: 'State',
      render: (r) => <PfChip tone={STATE_TONE[r.state] || 'idle'}>{r.state}</PfChip>,
    },
    { key: 'events_received', label: 'Events', align: 'right',
      render: (r) => num(r.events_received || 0) },
    { key: 'evidence_received', label: 'Evidence', align: 'right',
      render: (r) => num(r.evidence_received || 0) },
    // [2026-09-25] CHANGED — `orderSort: true`: the up/down date arrow, ordering the WHOLE result
    // set newest/oldest first through the server's `order` (see DataTable). This is the one
    // column the server sorts by (created_at).
    // OLD: { key: 'created_at', label: 'Started',
    { key: 'created_at', label: 'Started', orderSort: true,
      render: (r) => <span className="muted">{when(r.created_at)}</span> },
    { key: 'ended_at', label: 'Ended',
      render: (r) => <span className="muted">{r.ended_at ? when(r.ended_at) : '—'}</span> },
    // [2026-09-04] ADDED — the Figma's "Report" column.
    //
    // The whole row already opens the report (DataTable wires onRowClick, and the row is
    // keyboard-focusable), so this button is REDUNDANT BY DESIGN — and that is the point. A table
    // whose only affordance is "the row happens to be clickable" is one most people never discover;
    // an explicit control on each row is what tells them the report exists.
    //
    // stopPropagation, or the click runs twice: once here and once on the row underneath. Harmless
    // today (both open the same modal) and exactly the kind of thing that becomes a real bug the
    // day the row action and the button action differ.
    {
      key: 'report',
      label: 'Report',
      align: 'right',
      render: (r) => (
        <button className="pf-icon-btn sm" title="Open the full session report"
                aria-label={`Open the report for ${r.reference_id || r.session_id}`}
                onClick={(event) => { event.stopPropagation(); setOpenId(r.session_id) }}>
          <ReportIcon />
        </button>
      ),
    },
  ]

  return (
    <>
      {/* [2026-09-04] CHANGED — the card title and subtitle are dropped.
          OLD:
            title="Proctoring reports"
            subtitle="Every session your candidates ran. Click a row for the full timeline."
          The revamped shell renders "Proctoring Reports / Manage and track all your Proctoring
          Reports" in the page head directly above this card, so keeping these printed the heading
          twice, four lines apart. The one thing the old subtitle said that the page head does not —
          that a row opens the full timeline — is now on the rows themselves: they are `clickable`,
          keyboard-focusable, and the report column carries its own label. */}
      <DataTable
        columns={columns}
        fetchPage={fetchPage}
        pageSize={25}
        searchPlaceholder="Search candidate ref, session id or origin…"
        filters={[{
          key: 'state',
          label: 'State',
          options: [
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'ended', label: 'Ended' },
          ],
        }]}
        filterLabel="Reports"
        orderByName
        onRowClick={(r) => setOpenId(r.session_id)}
        rowKey={(r) => r.session_id}
        emptyTitle="No sessions yet"
        emptyHint="Sessions appear here as soon as candidates start proctored exams."
        csvName="proctoring-sessions"
      />

      {openId && (
        <ReportModal sessionId={openId} onClose={() => setOpenId(null)} onError={onError} />
      )}
    </>
  )
}
